<template>
  <div v-if="state.rules.visible" class="HRField">
    <hr-chips
      v-if="state.theType === 'select' || state.theType === 'radio'"
      :items="convertToChips(state.field.children)"
      v-model:value="state.field.value"
      @update:value="changeSelect"
      :disabled="state.field.validation.disabled || state.rules.disabled"
      :optionHints="state.optionHints"
      :errorMessage="state.field.fieldError"
    >
      {{ state.field.label }}
      <div
        v-if="state.field.help?.hint"
        class="HRField hr-chips__helper HRField__text-muted"
      >
        <p v-html="state.field.help?.hint"></p>
      </div>
    </hr-chips>

    <hr-field-content
      v-if="state.theType === 'content'"
      :content="state.field.label"
      :placeholder="state.field.help?.placeholder"
      :helper="state.field.help?.hint"
      :value="state.field"
      :invalid="state.field.isInvalid"
      @input="changeCheck"
    >
    </hr-field-content>

    <hr-check
      v-if="state.theType === 'check'"
      :label="state.field.label"
      :placeholder="state.field.help?.placeholder"
      :helper="state.field.help?.hint"
      :value="state.field.value === true ? true : false"
      :invalid="state.field.isInvalid"
      :errorMessage="state.field.fieldError"
      @input="changeCheck"
    ></hr-check>

    <template v-if="state.theType === 'address'">
      <hr-auto-complete
        v-if="!state.isAddressFieldsVisible"
        v-bind="$attrs"
        :value="state.field.value"
        :label="state.field.label"
        :placeholder="state.field.help?.placeholder"
        :external="state.fieldValidation.external"
        :before-input="state.fieldValidation.beforeInput"
        :invalid="state.field.isInvalid"
        :add="showManualAddressButton"
        class="HRField__hr-co-input HRField__address"
        @update="changeAutoCompleteAddress"
        @showAddressFields="showAddressFields()"
        @focus="focus"
        @blur="blur"
      >
        <template v-slot:invalid class="HRField__invalid">{{
          state.field.fieldError
        }}</template>
        <template v-slot:helper class="text-mutted">{{
          state.field.help?.hint || state.field.help?.placeholder
        }}</template>
      </hr-auto-complete>
      <template v-else>
        <hr-manual-address
          v-model="state.field"
          @input="changeInput"
          @focus="focus"
          :address-fields="getManualAddressFields()"
        />
      </template>
    </template>

    <hr-auto-complete
      v-if="state.theType === 'autocomplete'"
      :key="state.theKey"
      v-bind="$attrs"
      :value="state.field.value"
      :label="state.field.label"
      :placeholder="state.field.help?.placeholder"
      :external="state.fieldValidation.external"
      :before-input="state.fieldValidation.beforeInput"
      :invalid="state.field.isInvalid"
      :disabled="state.rules.disabled"
      class="HRField__hr-co-input"
      @update="changeAutoComplete"
      @focus="focus"
      @blur="blur"
    >
      <template v-slot:invalid class="HRField__invalid">{{
        state.field.fieldError
      }}</template>
      <template v-slot:helper class="text-mutted">{{
        state.field.help?.hint || state.field.help?.placeholder
      }}</template>
    </hr-auto-complete>

    <hr-input-phone
      v-if="state.theType === 'phone' || state.theType === 'e164_phone'"
      v-bind="$attrs"
      :value="state.field.value"
      :label="state.field.label"
      :placeholder="state.field.help?.placeholder"
      :disabled="state.field.validation.disabled || state.rules.disabled"
      :masked="state.masked"
      class="HRField__hr-co-input"
      @update="changePhone"
      @blur="validateField"
      @focus="focus"
    >
      <template v-slot:invalid class="HRField__invalid">{{
        state.field.fieldError
      }}</template>
      <template v-slot:helper class="text-mutted">{{
        state.field.help?.hint || state.field.help?.placeholder
      }}</template>
    </hr-input-phone>

    <template
      v-if="
        state.theType.match(
          /text|password|number|bsb|sortCode|alphaNumeric/g
        ) && state.field.type !== 'DATE_AU'
      "
    >
      <hr-co-input
        v-bind="$attrs"
        :type="getType()"
        :digit-group-size="getDigitGroupSize()"
        :auto-validation="!(modelValue.type === 'EMAIL')"
        :value="state.field.value"
        :label="state.field.label"
        :placeholder="state.field.help?.placeholder"
        :required="state.field.validation.required"
        :formatter="state.formatter"
        :unformatter="state.fieldValidation.unformatter"
        :validator="state.fieldValidation.validator"
        :invalid="state.field.isInvalid"
        :disabled="state.field.validation.disabled || state.rules.disabled"
        :max="modelValue.validation ? getMaxBankCodeLength() : null"
        :min="modelValue.validation ? modelValue.validation.minLength : null"
        :helper="state.field.help?.hint || state.field.help?.placeholder"
        :errorMessage="state.field.fieldError"
        :masked="state.masked"
        class="HRField__hr-co-input"
        @blur="blur"
        @focus="focus"
        @input="changeInput"
      >
        <template v-slot:invalid class="HRField__invalid">{{
          state.field.fieldError
        }}</template>
      </hr-co-input>

      <hr-co-input
        v-for="(child, index) in state.field.children"
        :key="index"
        :type="state.theType"
        :value="child.value"
        :label="child.label"
        :placeholder="state.field.help?.placeholder"
        :required="state.field.validation.required"
        :invalid="child.isInvalid"
        :disabled="state.field.validation.disabled"
        :masked="state.masked"
        v-bind="$attrs"
        class="HRField__hr-co-input HRField__hr-co-input-child"
        @blur="(value) => blurChild(child, value, index)"
        @focus="() => focusChild(child)"
        @input="(value) => changeChild(child, value, index)"
      >
        <template v-slot:invalid class="HRField__invalid">{{
          state.field.validation.errorMessage
        }}</template>
        <template v-slot:helper class="text-mutted">{{
          state.field.help?.hint || state.field.help?.placeholder
        }}</template>
      </hr-co-input>
    </template>

    <a
      v-if="state.theType === 'link'"
      :href="state.field.url"
      :target="state.field.newTab ? '_blank' : ''"
      >{{ state.field.label }}</a
    >

    <h-r-field-link-button
      v-if="state.theType === 'link_button'"
      :url="state.field.url"
      :icon="state.field.iconUrl"
      :title="state.field.label"
      :cta="state.field.help?.placeholder"
      :helper="state.field.help?.hint"
    ></h-r-field-link-button>

    <hr-field-date
      v-if="state.field.type === 'DATE_AU'"
      :value="state.field"
      @blur="blur"
      @focus="focus"
      :errorMessage="state.field.fieldError"
    ></hr-field-date>
  </div>
</template>

<script>
import HrCheck from '@/components/hr-check.vue';
import HrChips from '@/components/hr-chips.vue';
import HrFieldContent from '@/components/hr-field-content.vue';
import { getTypeValidator, typeTranslation } from '@/core/input-types/utils';
import HRFieldLinkButton from '@/components/HRFieldLinkButton';
import HrFieldDate from '@/components/hr-field-date.vue';
import HrAutoComplete from '@/components/hr-auto-complete.vue';
import HrInputPhone from '@/components/hr-input-phone.vue';
import HrCoInput from '@/components/hr-co-input.vue';
import HrManualAddress from '@/components/common/hr-manual-address.vue';
import { ADDRESS_FIELDS_UK } from '@/components/uk/address-fields';
import { ADDRESS_FIELDS_AU } from '@/components/au/address-fields';
import { reactive, watch, computed } from 'vue';
import store from '@/store';
import rulesFunctions from '@/store/composables/document-rules';
import { BANK_DETAILS, TAX_DETAILS, WORK_RIGHTS_UK_FIELDS } from '@/core/field-names';
import { validate } from '@/core/validation/validation';
import { COUNTRY } from '@/core/country';
import { enableMask } from '@/core/fields-to-mask.js';

const bsbDigitGroupSize = 3;
const sortCodeDigitGroupSize = 2;
const bsbMaxLength = 7;
const sortCodeMaxLength = 8;

export default {
  name: 'hrfield',
  components: {
    HrFieldContent,
    HRFieldLinkButton,
    HrCheck,
    HrFieldDate,
    HrAutoComplete,
    HrManualAddress,
    HrCoInput,
    HrChips,
    HrInputPhone,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const state = reactive({
      field: props.modelValue,
      theType: 'STRING',
      theValue: props.modelValue.value,
      theValueClean: props.modelValue.value,
      isInvalid: null,
      fieldValidation: {},
      rules: {
        visible: rulesFunctions.visible.default(),
        disabled: rulesFunctions.disabled.default(),
        override: rulesFunctions.override.default(),
      },
      isAddressFieldsVisible: false,
      theKey: computed(() => store.state.theKey), // TODO: remove this force update implementation
      documents: computed(() => store.state.documents),
      fieldRules: computed(() => store.state.Document.fieldRules),
      formatter: computed(() =>
        state.field?.type !== ('BSB_AU' || 'SORTCODE_UK')
          ? state.fieldValidation.formatter
          : null
      ),
      optionHints: null,
      masked: enableMask(props.modelValue),
    });
    const showManualAddressButton = computed(() => {
      if (state.theType === 'address') {
        return state.theType === 'address' ? 'Enter address manually' : null;
      }
      return null;
    });
    watch(
      () => store.state.documents,
      () => {
        checkRules();
      },
      {
        deep: true,
      }
    );
    watch(
      () => store.state.Document.fieldRulesTriggered,
      () => {
        checkRules();
      },
      {
        deep: true,
      }
    );
    async function initialize() {
      reduceOptionHintMap();

      if (state.field.validation && state.field.validation.rules) {
        // TODO:  here is adding repeated rules, a hack is implemented in the document store
        buildRules();
      }
      let type = await typeTranslation(props.modelValue.type);

      // TODO: fix this implementation
      if (type === 'check' && state.field.boolValue) {
        state.field.value = !!state.field.boolValue;
      }

      if (type.type && type.type === 'text' && type.validation === 'content') {
        state.theType = type.validation;
        return true;
      }

      if (typeof type === 'string') {
        state.theType = type;
        return true;
      }

      if (type.validation) {
        state.fieldValidation = await getTypeValidator(type.validation);
        state.field.validation = Object.assign(
          {},
          state.field.validation,
          state.fieldValidation
        );

        if (state.fieldValidation.type) {
          type.type = state.fieldValidation.type;
        }

        // fix initial value based on field type
        if (state.field.validation.beforeCreate) {
          const { value, valueObj } = await state.field.validation.beforeCreate(
            state.field
          );

          state.field.valueObj = valueObj || state.field.valueObj;
          state.field.value = value;

          input();
        }
      }
      state.theType = type.type;
    }

    function reduceOptionHintMap() {
      if (state.field?.type !== 'RADIO') {
        return;
      }
      state.optionHints = state.field.children.reduce((prev, curr) => {
        if (!curr?.help?.hint) {
          return { ...prev };
        }
        return {
          ...prev,
          [curr.value]: curr.help?.hint,
        };
      }, {});

      if (!Object.keys(state.optionHints).length) {
        state.optionHints = null;
      }
    }

    function convertToChips(items) {
      return items.map((item) => {
        return {
          id: item.value,
          description: item.label,
        };
      });
    }

    function focus() {
      emit('focus', state.field);
    }

    function focusChild(child) {
      emit('focus', child);
    }

    function buildRules() {
      const { rules } = state.field.validation;

      rules.forEach((rule) => {
        if (rulesFunctions[rule.type]) {
          state.rules[rule.type] = rulesFunctions[rule.type].default();
        }

        store.dispatch(
          'Document/addFieldRule',
          Object.assign({}, rule, { fieldName: state.field.name })
        );
      });
    }

    function checkRules() {
      const rules = state.fieldRules[state.field.name];
      if (rules) {
        const rulesIndexed = {};
        Object.keys(rules).forEach((target) => {
          const fieldRules = rules[target];

          fieldRules.forEach((fieldRule) => {
            rulesIndexed[fieldRule.type] = rulesIndexed[fieldRule.type] || [];
            rulesIndexed[fieldRule.type].push({
              value: store.getters['Document/getFieldValue'](target),
              rule: fieldRule,
            });
          });
        });
        Object.keys(rulesIndexed).forEach((rule) => {
          state.rules[rule] = rulesFunctions[rule].apply(rulesIndexed[rule]);
          state.field.appliedRules[rule] = state.rules[rule];
          store.dispatch('setFieldRules', state.field);
        });
      }
    }

    async function input() {
      checkRules();

      if (state.field.valueObj) {
        state.field.isInvalid = state.field.valueObj.isInvalid;
      }

      // TODO: abstract this solution for the fields who needs to be tested after blur event triggered
      if (props.modelValue.type === 'EMAIL') {
        const valid = state.field.validation.validator(state.field.value);
        state.field.isInvalid =
          valid === true || valid === false ? !valid : null;
      }

      if (
        state.field.validation.minLength &&
        state.field.validation.minLength > state.field.value.length
      ) {
        state.field.isInvalid = true;
      }

      if (!state.field.value) {
        state.field.isInvalid = null;
      }
      emit('update:model-value', state.field);
    }

    function blur(value, obj) {
      state.field.value = typeof value === 'undefined' ? null : value;
      state.field.valueObj = obj;
      //checking if the autocomplete is already filled, assigning the superfundAUValue to valueObj
      if (
        state.field.name === 'superannuation.fund' &&
        value &&
        !obj &&
        state.field.superfundAUValue
      ) {
        state.field.valueObj = state.field.superfundAUValue;
      }
      validateField();
      input();
    }

    function showAddressFields() {
      (state.isAddressFieldsVisible = true),
        (state.field.value = ''),
        (state.field.addressValue = ''),
        (state.field.isInvalid = null);
    }

    function changePhone(value) {
      if (!value.inputType) {
        const mobileValueFormat = {
          countryCode: value?.code,
          number: value?.number,
        };
        state.field.value = mobileValueFormat;
        state.field.valueObj = value;
        state.field.mobileValue = mobileValueFormat;
        emit('update:model-value', state.field);
      }
    }

    function changeInput() {
      state.field.isInvalid = null;
    }

    function change(value, obj) {
      state.field.value = value;
      state.field.valueObj = obj;
      input();
    }

    async function changeAutoComplete(value, valueObj) {
      state.field.isInvalid = !value ? null : false;

      state.field.value = value;
      state.field.valueObj = valueObj;
      state.field.superfundAUValue = valueObj;
      validateField();
      input();
    }

    function changeAutoCompleteAddress(value, obj) {
      state.field.value = value;

      if (Object.prototype.hasOwnProperty.call(obj, 'languageCode')) {
        state.field.isInvalid = false;
        state.field.value = value;
        state.field.valueObj = obj;
        state.field.addressValue = value;
      } else {
        state.field.isInvalid = null;
        state.field.value = value;
        state.field.valueObj = '';
        state.field.addressValue = '';
      }
      validateField();
      input();
    }

    function changeCheck(value) {
      state.field.value = value;
      state.field.valueObj = { unformatted: value, value };
      input();
      focus();
      validateField();
    }

    function changeSelect(value) {
      if (['employerNominated', 'employeeNominated'].includes(value)) {
        store.dispatch('setSuperfund', value);
      }
      state.field.value = value;
      state.field.valueObj = { unformatted: value, value };
      input();
      focus();
      validateField();
    }

    function blurChild(child, value, index) {
      // TODO: get this code more generic
      if (child.validation.linked && props.modelValue.type.match('PASSWORD')) {
        child.isInvalid = state.field.value !== value;
      }

      state.field.children.splice(index, 1, child);
    }

    function changeChild(child, value, index) {
      child.isInvalid = null;

      // TODO: get this code more generic
      if (child.validation.linked && props.modelValue.type.match('PASSWORD')) {
        child.isInvalid = state.field.value === value ? false : null;
      }

      state.field.children.splice(index, 1, child);
    }

    function getType() {
      if (state.field.type === 'BSB_AU') {
        return 'bsb';
      }
      if (state.field.name === BANK_DETAILS.SORT_CODE) {
        return 'sortCode';
      }
      if (state.field.type === 'TFN_AU') {
        return 'tfnAu';
      }
      if (
        state.field.name === TAX_DETAILS.NATIONAL_INSURANCE_NUMBER ||
        state.field.name === WORK_RIGHTS_UK_FIELDS.SHARE_CODE
      ) {
        return 'alphaNumeric';
      }
      return state.theType;
    }

    function getDigitGroupSize() {
      if (state.field?.type === 'BSB_AU') {
        return bsbDigitGroupSize;
      }
      if (state.field?.name === BANK_DETAILS.SORT_CODE) {
        return sortCodeDigitGroupSize;
      }
      return 0;
    }

    function getMaxBankCodeLength() {
      if (state.field?.type === 'BSB_AU') {
        return bsbMaxLength;
      }
      if (state.field?.name === BANK_DETAILS.SORT_CODE) {
        return sortCodeMaxLength;
      }
      return props.modelValue.validation.maxLength;
    }

    function validateField() {
      validate(state.field);
    }
    function getManualAddressFields() {
      switch (store.state.onboarding.countryCode) {
        case COUNTRY.GB:
          return ADDRESS_FIELDS_UK;
        default:
          return ADDRESS_FIELDS_AU;
      }
    }

    initialize();

    return {
      state,
      convertToChips,
      focus,
      focusChild,
      blur,
      changePhone,
      changeInput,
      change,
      changeAutoComplete,
      changeAutoCompleteAddress,
      changeCheck,
      changeSelect,
      blurChild,
      changeChild,
      getType,
      getDigitGroupSize,
      getMaxBankCodeLength,
      showAddressFields,
      showManualAddressButton,
      validate,
      validateField,
      getManualAddressFields,
    };
  },
};
</script>

<style lang="scss">
@import '@/core/style-vars.scss';

.HRField {
  margin-bottom: 30px;
  position: relative;
}

.HRField__hr-co-input input::placeholder {
  color: $gray-light;
}

.HRField__hr-co-input input,
.HRField__hr-co-input input:focus {
  height: 54px;
  border: 1px solid var(--co-lines-border-solid);
  font-size: inherit;
  outline: none;
  box-shadow: none;

  @media only screen and (max-width: 768px) {
    font-size: 16px;
  }
}

.HRField__hr-co-input .DSInput__icon {
  padding-right: 8px;
}

.HRField__hr-co-input .DSInput__password-toggle {
  color: $gray;
  font-weight: bold;
  font-size: 14px;
  line-height: 3.9;
}

.HRField__hr-co-input .DSInput__invalid-icon i {
  line-height: 2.8;
  color: $co-status-red;
}

.HRField__hr-co-input .DSInput__valid-icon i {
  line-height: 2.8;
  color: $co-status-green;
}

.HRField__hr-co-input .DSInput__label {
  color: #1e163c;
  font-size: 16px;
  line-height: 16px;
  font-weight: bold;
  margin-bottom: 10px;
}

.HRField__hr-co-input.helper .DSInput__label {
  margin-bottom: 0;
}

.HRField__hr-co-input-child {
  margin-top: 10px;
}

.HRField__hr-co-input .DSInput__invalid {
  color: $co-status-red;
  font-size: 14px;
  margin-top: 5px;
}

.HRField__hr-co-input .DSInputPhone__country {
  height: 54px;
}

.DSInputPhone__country {
  font-size: 16px;
  color: $gray;
}

.DSInput__label__optional {
  color: $gray;
  font-style: italic;
}

// DSInputPhone
.HRField .DSInputPhone__country {
  line-height: 2.5;
}

.DSInputPhone__country__no-selected {
  color: $gray-light;
}

.HRField .DSInputPhoneCountriesList__desktop__list__country {
  padding: 15px 10px;
  font-size: 16px;
  color: $gray;
}

.HRField .DSInputPhoneCountriesList__desktop__list__country div {
  padding: 0 2px;
}

.HRField .DSInputPhoneCountriesList__desktop__list {
  min-height: auto;
  height: auto;
  max-height: 166px;
}

.HRField .DSInputPhoneCountries .DSInput__input .DSIcon {
  color: $gray-light;
  margin-right: 5px;
}

.HRField .DSInputPhoneCountries .DSInput__input__input.icon {
  padding-right: 30px;
}

/* Hide Arrows in Number Input for Chrome, Safari, Edge, Opera*/
.HRField__hr-co-input input::-webkit-outer-spin-button,
.HRField__hr-co-input input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Hide Arrows in Number Input for Firefox*/
.HRField__hr-co-input input[type='number'] {
  -moz-appearance: textfield;
}

// hr-auto-complete
.HRField__hr-co-input .hr-auto-complete__helper {
  background: rgb(243, 245, 246); // TODO: needs the hex color to be replaced
  color: $gray;
  border: 1px solid $gray-light;
  border-top: none;
  padding: 3px 10px;
  font-weight: bold;
  position: absolute;
  z-index: 10;
  width: 100%;
}
.HRField__address .hr-auto-complete__helper {
  color: var(--co-text-highlight);
  padding: 10px;
}

.HRField__hr-co-input.hr-auto-complete .DSInput__input__input {
  padding-right: 100px !important;
}

.HRField__hr-co-input .hr-auto-complete__list__item {
  color: $gray;
  border-bottom: 1px solid $gray-light;
  padding: 10px 10px;

  &:last-child {
    border-bottom: none;
  }
}

.HRField__hr-co-input .DSInput__input__icon {
  color: $gray;
}

.HRField__hr-co-input .hr-auto-complete__list {
  background: $white;
  border: 1px solid $gray-light;
  border-top: none;
  border-bottom-right-radius: 8px;
  border-bottom-left-radius: 8px;

  position: absolute;
  z-index: 10;
  top: 101px;
  left: 0;
}

.HRField__address .hr-auto-complete__list {
  top: 118px;
}

.hr-auto-complete__list__item:focus {
  background: #f2fafe;
}

.hr-auto-complete__list__item:hover {
  background: #f2fafe;
}

.hr-auto-complete__input__icon {
  line-height: 2.7;
  font-weight: bold;
  color: $gray;
  padding-right: 10px;
}

.HRField .hr-chips__helper-icon {
  color: #1e163c;
}

.DSInput__input__input[disabled] {
  color: $gray-light;
}

.DSInputPhoneCountriesList .DSInput__icon .fa-search {
  font-size: 24px;
  line-height: 2.2;
}

.RadioFullWidth .hr-chips__bubble {
  width: 100%;
  margin-left: 0;
}

.flex-chips {
  display: flex;
  flex-wrap: wrap;
  position: relative;
}

.flex-chips .hr-chips__bubble:nth-child(2) {
  margin-left: 0;
}

.flex-chips .hr-chips__label {
  flex: 0 0 100%;
}

.RadioQuarterWidth .hr-chips__bubble {
  margin: 0;
  min-width: 80px;
}

.RadioHalfWidth .hr-chips__bubble {
  width: 47%;
}

.RadioThirdWidth .hr-chips__bubble {
  width: 30%;
}
.hr-chips__bubble {
  color: var(--co-text-primary);
}
.hr-chips__bubble.selected {
  background-color: var(--co-text-primary) !important;
  border-color: var(--co-text-primary) !important;
}

.hr-chips__label {
  color: var(--co-text-primary);
}

.HRField__text-muted {
  color: var(--co-text-secondary);
  font-size: 14px;
  line-height: 20px;
  margin: 10px 0 13px;
  font-weight: normal;
}

.co-input__valid {
  position: absolute;
  width: 18px;
  height: 20px;
  right: 4px;
  top: 20px;
  text-align: center;
  background-color: var(--co-accent-background-secondary);
  border-radius: 50%;
}

svg.co-input__valid-icon {
  color: var(--co-accent-secondary);
  height: 14px !important;
  width: 16px !important;
  padding: 2px;
}
</style>
